import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Pagination } from '@material-ui/lab';
import { keyBy, multiply } from 'lodash';
import { InvoiceModel, ProductOrderModel } from 'src/models/InvoiceModel';
import {
  Store,
  useAppDispatch,
} from 'src/redux/Store';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { getInvoiceAction } from 'src/redux/actions/invoiceActions';
import { useMediaQuery } from 'react-responsive';
import GuessPageHeader from 'src/components/Elements/Guess/GuessPageHeader';
import { Button } from '@material-ui/core';
import { MODALS } from 'src/models/ModalModel';
import { openModal } from 'src/redux/actions/modalActions';
import moment from 'moment-timezone-all';
import { getS3URL } from 'src/lib/S3File';

const useStyles = (isTabletOrMobile: boolean) => makeStyles((theme) => ({
  status: {
    width: '100%',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    gap: isTabletOrMobile ? theme.spacing(2) : theme.spacing(1),
  },
  col1: {
    flex: 1,
    flexDirection: 'column',
    flexBasis: '100%',
    width: '100%',
    alignItems: 'start',
  },
  col2: {
    flex: 1,
    flexDirection: 'column',
    flexBasis: isTabletOrMobile ? '100%' : '50%',
    alignItems: 'start',
  },
  pagination: {
    height: '4vh',
    paddingTop: '1vh',
    '& > ul': {
      justifyContent: 'center',
    },
  },
  pointer: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    border: '1px solid black',
    borderRadius: '3px',
    margin: `${theme.spacing(2)} 0 ${theme.spacing(2)}`,
  },
  alignRight: {
    textAlign: 'right',
  },
  alignLeft: {
    textAlign: 'left',
  },
  table: {
    width: '100%',
    '& thead': {
      backgroundColor: 'rgba(240, 240, 240, 1)',
    },
  },
  // Mobile-specific styles
  mobileSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    borderBottom: '1px solid #eee',
  },
  mobileLabel: {
    fontWeight: 'bold',
    fontSize: '0.75rem',
    color: '#666',
    textTransform: 'uppercase' as const,
    marginBottom: '2px',
  },
  mobileCard: {
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mobileRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${theme.spacing(0.5)} 0`,
  },
  mobileTotals: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'center' as const,
  },
}));

interface Props {
  setInvoiceId?: (id: number) => void;
  isGuess?: boolean;
}

const UserInvoiceDisplayer = (props: Props) => {
  const dispatch = useAppDispatch();
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 680px)' });
  const classes = useStyles(isTabletOrMobile)();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [page, setPage] = useState(1);
  const currentOrganisation = useSelector((state: Store) => state.organisation);

  const { setInvoiceId, isGuess } = props;

  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'CAD',
  });

  const selections = useSelector(
    (state: Store) => state.selected[ENTITIES.invoices] || [],
  );

  const invoice = useSelector(
    (state: Store) => {
      if (!!state.invoices[selections[currentIndex] as number]) {
        return state.invoices[selections[currentIndex] as number];
      }
      return {} as InvoiceModel;
    },
  );

  const lang = useSelector((state: Store) => state.language.language);
  const countries = useSelector((state: Store) => state.data.countries);

  const reloadInvoice = () => {
    if (selections[currentIndex]) {
      dispatch(getInvoiceAction(selections[currentIndex]));
    }
  };

  const handleOpen = () => {
    dispatch(openModal({
      modal: MODALS.guessPayment,
      payload: { invoice, forceReload: reloadInvoice },
    }));
  };

  useEffect(() => {
    dispatch(getInvoiceAction(selections[currentIndex]));
    if (setInvoiceId) {
      setInvoiceId(selections[currentIndex]);
    }
  }, [currentIndex]);

  const getCountries = (id: number) => {
    let index = 0;
    while (index < countries.length) {
      if (countries[index].id === id) {
        return countries[index][`name_${lang}`];
      }
      index += 1;
    }
    return '';
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setCurrentIndex(newPage - 1);
    if (setInvoiceId) {
      setInvoiceId(selections[newPage - 1]);
    }
  };

  const renderPagination = () => {
    if (selections.length < 2) return null;
    return (
      <Pagination
        className={classes.pagination}
        color="primary"
        count={selections.length}
        variant="outlined"
        shape="rounded"
        size="small"
        page={page}
        onChange={(e, newPage) => handlePageChange(newPage)} />
    );
  };

  const renderOrderProductName = (order: ProductOrderModel) => {
    if (order.product_id == null) return '';
    if (!!invoice.products && invoice.products.length > 0) {
      const currentProduct = keyBy(invoice.products, 'id')[order.product_id];
      if (!!currentProduct) {
        return currentProduct[`name_${lang ?? 'en'}`];
      }
    }
    return '';
  };

  const getName = () => {
    if (invoice.contact_company_name) {
      return invoice.contact_company_name;
    }
    return `${invoice.contact_first_name} ${invoice.contact_last_name}`;
  };

  const getDate = (value: any) => {
    if (!moment.isMoment(value)) {
      return moment(value).format('YYYY-DD-MM');
    }
    return value.format('YYYY-DD-MM');
  };

  const renderButtons = () => (
    <Button
      variant="contained"
      color="primary"
      size="small"
      onClick={handleOpen}
      disabled={!invoice.id}
      aria-label="Pay invoice"
      style={{ marginRight: '50px' }}>
      Pay
    </Button>
  );

  // ─── Shared sub-renders ──────────────────────────────────────────────────────
  const renderOrgAddress = () => (
    <>
      <b>{currentOrganisation[`name_${lang ?? 'en'}`]}</b><br />
      {currentOrganisation.address}, {currentOrganisation.suite}<br />
      {currentOrganisation.city}, {currentOrganisation.state}, {currentOrganisation.zipCode}<br />
      {getCountries(currentOrganisation.countryId ?? 0)}<br />
      {currentOrganisation.phone}<br />
      {currentOrganisation.tollfreePhone}
    </>
  );

  const renderOrgLogo = () => (
    <img
      width="150px"
      src={`${getS3URL()}${currentOrganisation.id}/images/organisation_logo.png?${performance.now()}`}
      alt="-" />
  );

  const renderInvoiceMeta = () => (
    <>
      <strong>{invoice.title}</strong><br />
      Invoice No: <strong>{invoice.invoice_number}</strong><br />
      Date: <strong>{getDate(invoice.issued_date)}</strong><br />
      Due Date: <strong>{getDate(invoice.due_date)}</strong><br />
    </>
  );

  const renderBillingAddress = () => (
    <>
      {getName()}<br />
      {invoice.billing_address} {invoice.billing_suite}<br />
      {invoice.billing_city}, {invoice.billing_state}, {invoice.billing_zip_code}<br />
      {getCountries(invoice.billing_country_id ?? 0)}<br />
    </>
  );

  const renderOrderRows = () => invoice.order?.map((order: ProductOrderModel, index: number) => (
    <tr key={`product-${order.product_id}-${index}`}>
      <td style={{ textAlign: 'center' }}>{renderOrderProductName(order)}</td>
      <td style={{ textAlign: 'center' }}>{order.quantity}X{formatter.format(order.paid ?? 0)}</td>
      <td style={{ textAlign: 'center' }}>{formatter.format(order.discount ?? 0)}</td>
      <td style={{ textAlign: 'center' }}>
        {formatter.format(multiply((order.paid ?? 0) - (order.discount ?? 0), order.quantity ?? 0))}
      </td>
    </tr>
  ));

  const renderTotals = () => (
    <>
      <span><b>Paid:</b> {formatter.format(invoice.paid ?? 0)}</span>
      <span><b>Scheduled:</b> {formatter.format(invoice.scheduled ?? 0)}</span>
      <span><b>Balance:</b> {formatter.format(invoice.balance ?? 0)}</span>
    </>
  );

  // ─── Desktop layout ──────────────────────────────────────────────────────────
  const renderUserInvoiceDesktop = () => (
    <>
      <div style={{ width: '100%' }}>
        {renderPagination()}
        {isGuess && <GuessPageHeader />}

        <table style={{ marginTop: '50px', width: '100%' }}>
          <tbody>
            <tr style={{ verticalAlign: 'top' }}>
              <td style={{ width: '40%' }}>{renderOrgAddress()}</td>
              <td style={{ width: '30%' }}>{renderOrgLogo()}</td>
              <td style={{ width: '30%', textAlign: 'right' }}>
                <div className={classes.col2}>{renderInvoiceMeta()}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <h3 style={{ margin: '0px' }}>Billing to</h3>
        <div className={classes.col2}>{renderBillingAddress()}</div>

        {!!invoice.products && (
          <div style={{ marginTop: '32px' }}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Qty X Price</th>
                  <th>Discount</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>{renderOrderRows()}</tbody>
              <tfoot>
                <tr><td colSpan={4}><hr aria-label="Section divider" /></td></tr>
                <tr>
                  <td colSpan={3} className={classes.alignRight}><i>Sub-total:</i></td>
                  <td colSpan={1} className={classes.alignRight}>
                    <i>{formatter.format(invoice.subtotal ?? 0)}</i>
                  </td>
                </tr>
                <tr><td colSpan={4}><hr aria-label="Section divider" /></td></tr>
                <tr>
                  <td colSpan={3} className={classes.alignRight}><b>Total:</b></td>
                  <td colSpan={1} className={classes.alignRight}>
                    <b>{formatter.format(invoice.total ?? 0)}</b>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div style={{
        marginTop: '50px',
        marginBottom: '50px',
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'center',
        gap: '30px',
      }}>
        {renderTotals()}
      </div>
      <div style={{ textAlign: 'center' }}>{renderButtons()}</div>
    </>
  );

  // ─── Mobile layout ───────────────────────────────────────────────────────────

  const renderUserInvoiceMobile = () => (
    <>
      {renderPagination()}
      {isGuess && <GuessPageHeader />}

      {/* Org + Logo stacked */}
      <div className={classes.mobileSection} style={{ textAlign: 'center' }}>
        {renderOrgLogo()}
        <div style={{ marginTop: '8px', fontSize: '0.9rem' }}>{renderOrgAddress()}</div>
      </div>

      {/* Invoice meta */}
      <div className={classes.mobileSection}>
        <div className={classes.mobileLabel}>Invoice Details</div>
        {renderInvoiceMeta()}
      </div>

      {/* Billing address */}
      <div className={classes.mobileSection}>
        <div className={classes.mobileLabel}>Billing To</div>
        {renderBillingAddress()}
      </div>

      {/* Order lines as cards */}
      {!!invoice.products && (
        <div className={classes.mobileSection}>
          <div className={classes.mobileLabel}>Items</div>
          {invoice.order?.map((order: ProductOrderModel, index: number) => (
            <div key={`mobile-product-${order.product_id}-${index}`} className={classes.mobileCard}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                {renderOrderProductName(order)}
              </div>
              <div className={classes.mobileRow}>
                <span>Qty × Price</span>
                <span>{order.quantity} × {formatter.format(order.paid ?? 0)}</span>
              </div>
              <div className={classes.mobileRow}>
                <span>Discount</span>
                <span>{formatter.format(order.discount ?? 0)}</span>
              </div>
              <div className={classes.mobileRow}>
                <span><b>Amount</b></span>
                <span>
                  <b>{ formatter.format(
                    multiply((order.paid ?? 0) - (order.discount ?? 0), order.quantity ?? 0),
                  )}
                  </b>
                </span>
              </div>
            </div>
          ))}

          {/* Totals summary */}
          <div className={classes.mobileRow} style={{ borderTop: '1px solid #ccc', paddingTop: '8px' }}>
            <span><i>Sub-total</i></span>
            <span><i>{formatter.format(invoice.subtotal ?? 0)}</i></span>
          </div>
          <div className={classes.mobileRow} style={{ borderTop: '1px solid #ccc', paddingTop: '8px' }}>
            <span><b>Total</b></span>
            <span><b>{formatter.format(invoice.total ?? 0)}</b></span>
          </div>
        </div>
      )}

      {/* Paid / Scheduled / Balance */}
      <div className={classes.mobileTotals}>
        {renderTotals()}
      </div>

      <div style={{ textAlign: 'center', marginBottom: '50px' }}>
        {renderButtons()}
      </div>
    </>
  );

  // ─── Entry point ─────────────────────────────────────────────────────────────

  return isTabletOrMobile ? renderUserInvoiceMobile() : renderUserInvoiceDesktop();
};

export default UserInvoiceDisplayer;
